<template>
  <!--
    The assistant asks here, and the answer lands in the record above — that is
    1d's whole claim, so the thread sits beside the fields rather than in a
    separate chat surface the user has to switch to.
  -->
  <section class="flex flex-col gap-3 p-4 bg-accent border border-accent-foreground/15 rounded-ds-lg">
    <div class="flex gap-3">
      <span class="inline-flex items-center justify-center size-6.5 shrink-0 bg-primary text-primary-foreground rounded-ds-sm">
        <Sparkles class="size-3.5" />
      </span>
      <p class="text-sm font-medium leading-normal text-accent-foreground">
        {{ question }}
      </p>
    </div>

    <ul
      v-if="suggestions.length"
      class="flex flex-wrap gap-2 pl-9.5"
    >
      <li
        v-for="suggestion in suggestions"
        :key="suggestion"
      >
        <button
          type="button"
          class="px-2.5 py-1 text-xs font-bold text-accent-foreground cursor-pointer
            bg-card border border-accent-foreground/20 rounded-full
            transition-colors duration-ds-fast ease-ds-standard
            hover:bg-accent-foreground/10
            focus-visible:outline-none focus-visible:shadow-ds-focus"
          @click="emit('suggest', suggestion)"
        >
          {{ suggestion }}
        </button>
      </li>
    </ul>

    <form
      class="ml-9.5"
      @submit.prevent="submit"
    >
      <DSInputGroup>
        <DSInputGroupInput
          v-model="answer"
          type="text"
          :placeholder
          aria-label="Answer the assistant"
        />
        <DSInputGroupAddon align="inline-end">
          <DSInputGroupButton
            type="submit"
            variant="primary"
            size="sm"
            :disabled="!isSubmittable"
            aria-label="Send answer"
          >
            <ArrowUp />
          </DSInputGroupButton>
        </DSInputGroupAddon>
      </DSInputGroup>
    </form>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { ArrowUp, Sparkles } from '@lucide/vue'

import {
  DSInputGroup,
  DSInputGroupAddon,
  DSInputGroupButton,
  DSInputGroupInput,
} from '@ds'

defineProps<{
  /** What the assistant is asking for, in its own words. */
  question: string
  /** One-tap answers. Each is sent as if typed. */
  suggestions: string[]
  placeholder?: string
}>()

const emit = defineEmits<{
  (e: 'suggest', suggestion: string): void
  (e: 'answer', text: string): void
}>()

const answer = ref('')

const isSubmittable = computed(() => !!answer.value.trim())

const submit = () => {
  if (!isSubmittable.value) return

  emit('answer', answer.value.trim())
  answer.value = ''
}
</script>
